import type { GeoLocation } from '../providers/LocationProvider';
import { __ } from '../utils';
import {
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  type SxProps,
  type Theme,
} from '@presov.sk/presov-ui-react';
import CloseIcon from '@mui/icons-material/Close';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ImageIcon from '@mui/icons-material/Image';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import React from 'react';

export type GpsUsageState = 'unused' | 'draft' | 'used';

type FilesListProps = {
  files: {
    name: string;
    type?: string;
    size?: number;
    location?: { longitude: number; latitude: number } | GeoLocation;
  }[];
  onPreview?: (index: number) => void;
  onRemove?: (index: number) => void | Promise<void>;
  onLocate?: (position: GeoLocation) => void;
  onSelectFile?: (fileId: string | null) => void;
  sx?: SxProps<Theme>;
  gpsUsageState?: Record<string, GpsUsageState>; // filename -> state
  onGpsClick?: (fileName: string, gpsLocation: GeoLocation) => void;
};

const FilesList: React.FC<FilesListProps> = ({
  files,
  onPreview,
  onRemove,
  sx,
  onLocate,
  onSelectFile,
  gpsUsageState,
  onGpsClick,
}) => {
  const { palette } = useTheme();
  const [removing, setRemoving] = React.useState<number>();

  return (
    <Stack gap={1.5} sx={sx}>
      {files.map((file, i) => {
        const selectedFileStyles = {
          bgcolor: `${palette.primary.main} !important`,
          color: 'white',
        };

        const draftFileStyles = {
          border: `2px solid ${palette.primary.main} !important`,
          color: `${palette.primary.main} !important`,
        };

        const unusedGpsStyles = {}; // Default style

        // Determine GPS button style based on usage state
        const gpsState = gpsUsageState?.[file.name];
        let gpsButtonStyle = unusedGpsStyles;

        if (gpsState === 'draft') {
          gpsButtonStyle = draftFileStyles;
        } else if (gpsState === 'used') {
          gpsButtonStyle = selectedFileStyles;
        }

        return (
          <Stack
            key={i}
            p={1}
            gap={1}
            borderRadius={1}
            direction='row'
            bgcolor={'#f5f5f5'}
          >
            <Stack
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: (theme) => theme.palette.background.paper,
                border: (theme) =>
                  `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {file.type.startsWith('image/') ? (
                <ImageIcon sx={{ fontSize: 24 }} />
              ) : file.type.startsWith('video/') ? (
                <OndemandVideoIcon sx={{ fontSize: 24 }} />
              ) : (
                <FilePresentIcon sx={{ fontSize: 24 }} />
              )}
            </Stack>
            <Stack
              flex={1}
              gap={0.25}
              justifyContent={'center'}
              sx={{
                flexShrink: 1,
              }}
            >
              <Typography
                variant='caption'
                fontWeight={'bold'}
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-all',
                  mb: -0.5,
                }}
              >
                {file.name}
              </Typography>
              {file.size !== undefined && (
                <Typography
                  variant='caption'
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                  }}
                >
                  {`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                </Typography>
              )}
            </Stack>
            <Stack
              direction={'row'}
              alignItems={'center'}
              gap={0.5}
              sx={{
                flexShrink: 0,
              }}
            >
              {file.location && onGpsClick && (
                <Tooltip title={__('Poloha bola zistená z metadát súboru')}>
                  <IconButton
                    color='primary'
                    size='small'
                    sx={{
                      ...gpsButtonStyle,
                      width: 32,
                      height: 32,
                    }}
                    onClick={() => {
                      onSelectFile?.(file.name);

                      // Handle both object format {longitude, latitude} and tuple format [lon, lat]
                      let geoLocation: GeoLocation;
                      if (Array.isArray(file.location)) {
                        geoLocation = file.location;
                      } else {
                        geoLocation = [
                          file.location.longitude,
                          file.location.latitude,
                        ];
                      }

                      onLocate?.(geoLocation);
                      onGpsClick?.(file.name, geoLocation);
                    }}
                  >
                    <GpsFixedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onPreview && file.type !== 'image/heic' && (
                <Button
                  variant='outlined'
                  onClick={() => onPreview(i)}
                  size='small'
                  sx={{
                    alignSelf: 'center',
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (theme) => theme.palette.background.paper,
                    color: (theme) => theme.palette.text.primary,
                    borderColor: (theme) => theme.palette.divider,
                    '&:hover': {
                      borderColor: (theme) => theme.palette.text.primary,
                    },
                  }}
                >
                  {__(`Zobraziť`)}
                </Button>
              )}
              {onRemove && (
                <IconButton
                  size='small'
                  sx={{
                    width: 32,
                    height: 32,
                  }}
                  disabled={removing !== undefined}
                  loading={removing === i}
                  onClick={async () => {
                    setRemoving(i);
                    try {
                      await onRemove(i);
                    } finally {
                      setRemoving(undefined);
                    }
                  }}
                >
                  <CloseIcon fontSize='medium' />
                </IconButton>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default FilesList;
